export const ARTICLE_CATEGORIES = ["React", "Go", "Data", "Cloud", "AI"] as const;
export type ArticleCategory = (typeof ARTICLE_CATEGORIES)[number];

export interface ArticleMeta {
  slug: string; // must match the .tsx filename
  title: string;
  summary: string; // card text, meta description, og:description
  category: ArticleCategory;
  tags: string[];
  publishedAt: string; // ISO 8601 with timezone, e.g. "2026-10-20T09:00:00+08:00"
  updatedAt: string;
  readingMinutes: number; // set by hand, checked by a test
  cover?: ArticleCover; // hero image and og:image; falls back to the site image
}

export interface ArticleCover {
  src: string; // site-root path, e.g. "/images/articles/slug.webp"
  alt: string;
  width: number;
  height: number;
}

export const ARTICLES: ArticleMeta[] = [
  {
    slug: "ai-orchestration-in-typescript",
    title: "AI Orchestration in TypeScript: Workflows, Tools and Agents",
    summary:
      "How I structure AI features in TypeScript on Node: workflows before agents, schema-validated model output, typed and scoped tools, agent loops with hard limits, timeouts and retries, queued runs that resume, and tracing, budgets and evals.",
    category: "AI",
    tags: ["ai", "llm", "orchestration", "typescript", "nodejs", "agents"],
    publishedAt: "2026-10-25T13:30:00+08:00",
    updatedAt: "2026-10-25T13:30:00+08:00",
    readingMinutes: 10,
    cover: {
      src: "/images/articles/ai-orchestration-in-typescript.webp",
      alt: "A glowing central orchestrator node connected to a model, a tool, a database, a queue and a safety check",
      width: 1280,
      height: 720,
    },
  },
  {
    slug: "docker-production-images",
    title: "Production-Ready Docker Images for Go and Node",
    summary:
      "How I build small, fast-to-rebuild and safer Docker images: multi-stage builds, distroless and Alpine bases, layer caching, a strict .dockerignore, non-root users, self-checking health checks and image scanning.",
    category: "Cloud",
    tags: ["docker", "go", "nodejs", "containers", "security", "ci-cd"],
    publishedAt: "2026-10-12T21:40:00+08:00",
    updatedAt: "2026-10-16T08:55:00+08:00",
    readingMinutes: 11,
  },
  {
    slug: "deploying-react-and-go-on-aws",
    title: "Deploying a React + Go + PostgreSQL App on AWS",
    summary:
      "How I ship a React SPA, a Go API and PostgreSQL on AWS: S3 and CloudFront for the front end, Go on ECS Fargate behind an ALB, RDS in private subnets, environment separation, a GitHub Actions pipeline and the costs that never reach zero.",
    category: "Cloud",
    tags: ["aws", "cloudfront", "ecs-fargate", "rds", "ci-cd", "deployment"],
    publishedAt: "2026-10-06T20:15:00+08:00",
    updatedAt: "2026-10-06T20:15:00+08:00",
    readingMinutes: 11,
  },
  {
    slug: "aws-secure-baseline-and-iam",
    title: "AWS Security Baseline: IAM, Secrets and Networking",
    summary:
      "The baseline I set up in a new AWS account before deploying anything: roles instead of keys, least-privilege IAM policies, secrets fetched at runtime, private subnets with chained security groups, and CloudTrail, budgets and alarms.",
    category: "Cloud",
    tags: [
      "aws",
      "iam",
      "secrets-manager",
      "security-groups",
      "cloudtrail",
      "security",
    ],
    publishedAt: "2026-09-30T19:30:00+08:00",
    updatedAt: "2026-10-09T22:10:00+08:00",
    readingMinutes: 11,
  },
  {
    slug: "postgresql-schema-indexing-and-migrations",
    title: "PostgreSQL: Schema, Indexes and Safe Migrations",
    summary:
      "How I design PostgreSQL schemas and change them without downtime: constraints and types, composite and partial indexes, reading EXPLAIN ANALYZE, pooling and transactions from Go, and expand-and-contract migrations.",
    category: "Data",
    tags: ["postgresql", "sql", "indexes", "migrations", "pgx", "performance"],
    publishedAt: "2026-09-26T10:20:00+08:00",
    updatedAt: "2026-09-26T10:20:00+08:00",
    readingMinutes: 11,
  },
  {
    slug: "go-concurrency-errors-and-context",
    title: "Go Concurrency, Errors and context",
    summary:
      "The habits that keep my Go services from leaking goroutines or losing error causes: goroutine owners and exits, errgroup, channels vs. mutexes, context timeouts, %w wrapping with errors.Is and errors.As, and table-driven tests with -race.",
    category: "Go",
    tags: ["go", "concurrency", "context", "errgroup", "errors", "testing"],
    publishedAt: "2026-09-23T21:05:00+08:00",
    updatedAt: "2026-10-03T09:35:00+08:00",
    readingMinutes: 9,
  },
  {
    slug: "go-project-structure-and-http-apis",
    title: "Structuring a Go HTTP API",
    summary:
      "The layout I start every Go HTTP service with: cmd and internal, handler, service and repository layers, small consumer-defined interfaces, environment config, middleware and graceful shutdown, using only the standard library.",
    category: "Go",
    tags: ["go", "http", "architecture", "graceful-shutdown", "middleware"],
    publishedAt: "2026-09-21T20:45:00+08:00",
    updatedAt: "2026-09-21T20:45:00+08:00",
    readingMinutes: 9,
  },
  {
    slug: "accessible-and-seo-friendly-react",
    title: "Accessibility and SEO in a React SPA",
    summary:
      "The fixes I apply to React single-page apps on client projects: semantic HTML, ARIA only when needed, focus and title on route changes, per-route metadata, crawler-visible HTML, sitemap and robots.",
    category: "React",
    tags: ["accessibility", "seo", "react", "a11y", "open-graph"],
    publishedAt: "2026-09-19T11:10:00+08:00",
    updatedAt: "2026-10-14T20:25:00+08:00",
    readingMinutes: 10,
  },
  {
    slug: "typescript-for-react-apps",
    title: "Practical TypeScript for React Apps",
    summary:
      "The TypeScript habits I apply on every React project: strict mode, typed props and hooks, discriminated unions for UI state, unknown over any, validated API responses and typed TanStack Router params.",
    category: "React",
    tags: ["typescript", "react", "tanstack-router", "strict", "types"],
    publishedAt: "2026-09-18T19:50:00+08:00",
    updatedAt: "2026-09-18T19:50:00+08:00",
    readingMinutes: 8,
  },
  {
    slug: "react-performance-essentials",
    title: "React Performance: Measure First, Then Optimize",
    summary:
      "How I find and fix slow React apps on client projects: profile first, trim the bundle and images, fix structure and keys, and add memo, useMemo and useCallback only where a profile justifies them.",
    category: "React",
    tags: ["react", "performance", "vite", "profiler", "code-splitting"],
    publishedAt: "2026-09-17T21:15:00+08:00",
    updatedAt: "2026-09-17T21:15:00+08:00",
    readingMinutes: 9,
  },
  {
    slug: "react-component-and-state-design",
    title: "React Component and State Design That Scales",
    summary:
      "The rules I use on client projects to keep React state small and local: derive instead of sync, colocate, lift only when needed, use Zustand selectors, and wrap effects in custom hooks.",
    category: "React",
    tags: ["react", "state", "zustand", "hooks", "typescript"],
    publishedAt: "2026-09-16T20:30:00+08:00",
    updatedAt: "2026-09-16T20:30:00+08:00",
    readingMinutes: 8,
  },
];
